import { useTranslations } from '@vocab/react';
import { Hidden, Stack } from 'braid-design-system';
import { memo, useState } from 'react';

import {
  RemoveUserDialog,
  type UserToRemove,
} from 'src/App/Components/RemoveUserDialog/RemoveUserDialog';
import { useTalentSearchContext } from 'src/hooks/use-talent-search-context';
import { useUserActions } from 'src/hooks/use-user-actions';

import { useSearchUsers } from '../SearchUsers/SearchUsers';

import translations from './.vocab';
import { SearchEmptyState } from './EmptyState/EmptyState';
import { Table, Th, Tr } from './Table/Table';
import { UserRow, UserCard } from './UserItem/UserItem';

import * as styles from './UsersList.css';

export const UsersList = memo(() => {
  const { searchResults, searchTerm } = useSearchUsers();
  const { accountHasPremiumTalentSearch } = useTalentSearchContext();
  const [userToRemove, setUserToRemove] = useState<UserToRemove | null>(null);
  const { handleRemoveUser } = useUserActions();
  const { t } = useTranslations(translations);

  const removeUser = (user: UserToRemove) => {
    if (!userToRemove) {
      setUserToRemove(user);
      return;
    }
    if (user.id === userToRemove.id) {
      handleRemoveUser(Number(user.id));
    }
    setUserToRemove(null);
  };

  const users = searchResults;

  return (
    <>
      <Hidden below="tablet">
        <Table>
          <thead>
            <Tr>
              <Th
                paddingLeft="medium"
                paddingY="small"
                className={styles.userColumn}
              >
                {t('Team member column')}
              </Th>
              <Th paddingX="medium">{t('Permissions column')}</Th>
              <Th
                textAlign="right"
                paddingRight="medium"
                className={styles.actionsColumn}
              >
                {t('Actions column')}
              </Th>
            </Tr>
          </thead>
          <tbody>
            {users?.map((user) => (
              <UserRow
                key={`user-row-${user.id}`}
                user={user}
                removeUser={removeUser}
                accountHasPremiumTalentSearch={accountHasPremiumTalentSearch}
              />
            ))}
          </tbody>
        </Table>
      </Hidden>
      <Hidden above="mobile">
        <Stack space="medium">
          {users?.map((user) => (
            <UserCard
              key={`user-row-${user.id}`}
              user={user}
              removeUser={removeUser}
              accountHasPremiumTalentSearch={accountHasPremiumTalentSearch}
            />
          ))}
        </Stack>
      </Hidden>
      <RemoveUserDialog
        userToRemove={userToRemove}
        removeUser={removeUser}
        setUserToRemove={setUserToRemove}
      />
      <>{searchTerm && users?.length === 0 && <SearchEmptyState />}</>
    </>
  );
});
